import { useEffect, useMemo, useState } from 'react';
import { etapaDeEntrada, gruposDoFunil } from '../data/etapas';
import aviso from '../lib/aviso';
import RecordPanel, { PanelSection } from './RecordPanel';
import Select, { iniciaisDe } from './Select';
import OrganizationPicker from './OrganizationPicker';
import { apiFetch } from '../services/api';
import { ContactService, type Contact } from '../services/ContactService';
import {
    OpportunityService, type CreateOpportunityPayload, type CreateOpportunityCampoPersonalizado,
} from '../services/OpportunityService';
import CampoPersonalizadoService, {
    TipoCampoPersonalizado, type CampoPersonalizado,
} from '../services/CampoPersonalizadoService';

const apiUrl = import.meta.env.VITE_API_URL;

/**
 * Etapas em que uma oportunidade pode NASCER. Novos (IA) fica de fora porque
 * a API não deixa oportunidade manual entrar na IA (ChatService.cs:351);
 * Ganho exige produto vinculado e Perdido exige motivo — as duas só se
 * alcançam arrastando. Mesma lista que a API valida no `Criar`.
 */
/* O miolo do funil da empresa — etapa que ela criar entra aqui sozinha, e a API valida a mesma
   lista. As do robô e os dois desfechos ficam de fora: oportunidade não nasce ganha nem na IA. */
export const etapasCriaveis = () => gruposDoFunil()
    .filter(g => g.plus)
    .map(g => ({ value: String(g.alvo), label: g.title, dot: `var(--pl-st-${g.cor})` }));

const TEMPERATURAS = [
    { value: '1', label: 'Quente', dot: 'var(--pl-t-hot)' },
    { value: '2', label: 'Morno', dot: 'var(--pl-t-warm)' },
    { value: '3', label: 'Frio', dot: 'var(--pl-t-cold)' },
];

const BOOLEANO = [
    { value: 'true', label: 'Sim' },
    { value: 'false', label: 'Não' },
];

interface Usuario { id: number; nome: string }

interface Props {
    open: boolean;
    onClose: () => void;
    onCreated: () => void;
    /** Etapa pré-escolhida — vem do "+" da coluna. */
    statusInicial?: number;
    usuarioAtual: Usuario | null;
}

const formatarMoeda = (valor: string) => {
    const digitos = valor.replace(/\D/g, '');
    if (!digitos) return '';
    return (Number(digitos) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatarTelefone = (bruto: string) => {
    let v = bruto.replace(/\D/g, '');
    if (v.length <= 11) {
        if (v.length > 6) {
            return v.length === 11
                ? `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7)}`
                : `(${v.slice(0, 2)}) ${v.slice(2, 6)}-${v.slice(6)}`;
        }
        return v.length > 2 ? `(${v.slice(0, 2)}) ${v.slice(2)}` : v;
    }
    if (v.length > 13) v = v.slice(0, 13);
    return v.length <= 12
        ? `+${v.slice(0, 2)} (${v.slice(2, 4)}) ${v.slice(4, 8)}-${v.slice(8)}`
        : `+${v.slice(0, 2)} (${v.slice(2, 4)}) ${v.slice(4, 9)}-${v.slice(9)}`;
};

/**
 * "Nova oportunidade" no `RecordPanel`, em modo Novo — substitui a modal de
 * dois passos. Os campos são os que a modal já mandava para
 * `OportunidadeVenda/Criar`, mais **Etapa** (novo na API) e **Responsável**
 * (já existia no payload, o formulário é que não mostrava).
 */
const CreateOpportunityPanel = ({ open, onClose, onCreated, statusInicial, usuarioAtual }: Props) => {
    const [modoContato, setModoContato] = useState<'existente' | 'novo'>('existente');
    const [contatos, setContatos] = useState<Contact[]>([]);
    const [contatoId, setContatoId] = useState('');
    /** Organização do negócio. Com ela, o contato é opcional (§4.2). */
    const [organizacao, setOrganizacao] = useState<{ id: number | null; nome: string | null }>({ id: null, nome: null });
    const [nome, setNome] = useState('');
    const [telefone, setTelefone] = useState('');
    const [email, setEmail] = useState('');

    const [etapa, setEtapa] = useState(() => String(etapaDeEntrada()));
    const [temperatura, setTemperatura] = useState('');
    const [valor, setValor] = useState('');
    const [responsavelId, setResponsavelId] = useState('');
    const [usuarios, setUsuarios] = useState<Usuario[]>([]);
    const [observacao, setObservacao] = useState('');

    const [campos, setCampos] = useState<CampoPersonalizado[]>([]);
    const [valoresCampos, setValoresCampos] = useState<Record<number, string>>({});

    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (!open) return;
        setModoContato('existente');
        setContatoId(''); setNome(''); setTelefone(''); setEmail('');
        setOrganizacao({ id: null, nome: null });
        setEtapa(String(statusInicial ?? etapaDeEntrada()));
        setTemperatura(''); setValor(''); setObservacao('');
        setResponsavelId(usuarioAtual ? String(usuarioAtual.id) : '');
        setValoresCampos({});
        setSalvando(false);

        ContactService.getAll()
            .then(r => setContatos(r.sucesso && Array.isArray(r.dados) ? r.dados : []))
            .catch(() => setContatos([]));
        CampoPersonalizadoService.buscarTodos()
            .then(c => setCampos([...c].sort((a, b) => a.ordem - b.ordem)))
            .catch(() => setCampos([]));
        apiFetch(`${apiUrl}/api/Usuario/BuscarTodos`)
            .then(r => r.json())
            .then(r => setUsuarios(
                r?.sucesso && Array.isArray(r.dados)
                    ? r.dados.map((u: { userId: number; userName: string }) => ({ id: u.userId, nome: u.userName }))
                    : [],
            ))
            .catch(() => setUsuarios([]));
    }, [open, statusInicial, usuarioAtual]);

    const opcoesContato = useMemo(() => contatos.map(c => ({
        value: String(c.contatoId),
        label: c.nome || c.telefone || `Contato ${c.contatoId}`,
        meta: c.telefone || undefined,
        avatar: iniciaisDe(c.nome || '?'),
    })), [contatos]);

    const opcoesUsuario = useMemo(() => {
        const lista = usuarios.length > 0 || !usuarioAtual ? usuarios : [usuarioAtual];
        return lista.map(u => ({ value: String(u.id), label: u.nome, avatar: iniciaisDe(u.nome) }));
    }, [usuarios, usuarioAtual]);

    const validar = (): string | null => {
        if (modoContato === 'existente' && !contatoId && !organizacao.id) return 'Escolha um contato ou uma organização para a oportunidade.';
        if (modoContato === 'novo' && !nome.trim() && !organizacao.id) return 'Informe o nome do contato ou escolha uma organização.';
        for (const campo of campos) {
            if (campo.obrigatorio && !(valoresCampos[campo.campoPersonalizadoId] ?? '').trim()) {
                return `O campo "${campo.nome}" é obrigatório.`;
            }
        }
        return null;
    };

    const montarPayload = (): CreateOpportunityPayload => {
        const preenchidos: CreateOpportunityCampoPersonalizado[] = campos
            .filter(c => (valoresCampos[c.campoPersonalizadoId] ?? '').trim() !== '')
            .map(c => ({ campoPersonalizadoId: c.campoPersonalizadoId, valor: valoresCampos[c.campoPersonalizadoId] }));
        const digitos = valor.replace(/\D/g, '');
        return {
            contatoNovo: modoContato === 'novo' && !!nome.trim(),
            contatoId: modoContato === 'novo' || !contatoId ? null : Number(contatoId),
            contato: modoContato === 'novo' && nome.trim()
                ? { nome: nome.trim(), telefone: telefone.replace(/\D/g, '') || undefined, email: email.trim() || undefined }
                : null,
            usuarioResponsavelId: responsavelId ? Number(responsavelId) : null,
            tipoLeadId: temperatura ? Number(temperatura) : null,
            valor: digitos ? Number(digitos) / 100 : null,
            observacao: observacao.trim() || null,
            camposPersonalizados: preenchidos.length > 0 ? preenchidos : null,
            statusId: Number(etapa),
            organizacaoId: organizacao.id,
        };
    };

    const criar = async () => {
        const erro = validar();
        if (erro) { aviso.atencao(erro); return; }
        setSalvando(true);
        try {
            const r = await OpportunityService.createOpportunity(montarPayload());
            if (r.sucesso) {
                aviso.ok('Oportunidade criada.');
                onCreated();
                onClose();
            } else {
                aviso.erro(r.mensagem || 'Não foi possível criar a oportunidade.');
            }
        } catch {
            aviso.erro('Não foi possível criar a oportunidade.');
        } finally {
            setSalvando(false);
        }
    };

    const campoPersonalizado = (campo: CampoPersonalizado) => {
        const v = valoresCampos[campo.campoPersonalizadoId] ?? '';
        const set = (nv: string) => setValoresCampos(prev => ({ ...prev, [campo.campoPersonalizadoId]: nv }));
        if (campo.tipo === TipoCampoPersonalizado.Selecao) {
            return <Select value={v} onChange={set} placeholder="Selecionar" disabled={salvando}
                options={(campo.opcoes ?? []).map(op => ({ value: op, label: op }))} />;
        }
        if (campo.tipo === TipoCampoPersonalizado.Booleano) {
            return <Select value={v} onChange={set} placeholder="Selecionar" disabled={salvando} options={BOOLEANO} />;
        }
        const tipo = campo.tipo === TipoCampoPersonalizado.Data ? 'date'
            : campo.tipo === TipoCampoPersonalizado.Numero ? 'number' : 'text';
        return <input type={tipo} step={tipo === 'number' ? '0.01' : undefined} className="rp-input"
            value={v} onChange={e => set(e.target.value)} disabled={salvando} />;
    };

    const tituloContato = (modoContato === 'novo'
        ? nome.trim()
        : contatos.find(c => String(c.contatoId) === contatoId)?.nome) || organizacao.nome || '';

    /* Escolher um contato que já pertence a uma organização preenche a organização
       do negócio — é a herança da API, mostrada antes de salvar. Nunca sobrescreve
       uma organização já escolhida à mão. */
    const escolherContato = (v: string) => {
        setContatoId(v);
        const c = contatos.find(x => String(x.contatoId) === v);
        if (c?.organizacaoId && !organizacao.id) setOrganizacao({ id: c.organizacaoId, nome: c.organizacaoNome ?? null });
    };

    return (
        <RecordPanel
            open={open}
            onClose={onClose}
            title={tituloContato || 'Nova oportunidade'}
            editing
            footer={
                <>
                    <button type="button" className="rp-btn" onClick={onClose} disabled={salvando}>Cancelar</button>
                    <button type="button" className="rp-btn is-primary" onClick={criar} disabled={salvando}>
                        {salvando ? 'Criando…' : 'Criar oportunidade'}
                    </button>
                </>
            }
        >
            <PanelSection
                title="Contato"
                extra={
                    <div className="pl-seg" role="group" aria-label="Tipo de contato" style={{ height: 28 }}>
                        <button type="button" className={modoContato === 'existente' ? 'is-active' : ''}
                            onClick={() => setModoContato('existente')}>Existente</button>
                        <button type="button" className={modoContato === 'novo' ? 'is-active' : ''}
                            onClick={() => setModoContato('novo')}>Novo</button>
                    </div>
                }
            >
                {modoContato === 'existente' ? (
                    <div className="rp-field">
                        <Select
                            value={contatoId}
                            onChange={escolherContato}
                            options={opcoesContato}
                            placeholder={organizacao.id ? 'Contato (opcional) — buscar por nome ou telefone…' : 'Buscar contato por nome ou telefone…'}
                            searchFrom={1}
                            showOptionAccessory
                            disabled={salvando}
                            emptyText="Nenhum contato com esse nome"
                        />
                        {/* Aviso antes de criar: oportunidade aberta de propósito traz o contato de volta ao funil. */}
                        {contatoId && contatos.find(c => String(c.contatoId) === contatoId)?.foraDoFunil && (
                            <p className="rp-none" style={{ marginTop: 6 }}>
                                Este contato está fora do funil; criar a oportunidade o traz de volta.
                            </p>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Mesmo aviso do modo "Existente": o telefone digitado já é de um contato fora do funil.
                            Mesma regra do servidor (telefone exato contra os dígitos enviados), para não avisar de um
                            contato que a API não vai achar; usa a lista que o painel já carrega (sem consulta nova). */}
                        {(() => {
                            const digitos = telefone.replace(/\D/g, '');
                            const marcado = digitos.length >= 8 && contatos.some(c => c.foraDoFunil && c.telefone === digitos);
                            return marcado ? (
                                <p className="rp-none" style={{ marginBottom: 6 }}>
                                    Este contato está fora do funil; criar a oportunidade o traz de volta.
                                </p>
                            ) : null;
                        })()}
                        <div className="rp-field">
                            <label className="rp-label" htmlFor="pl-novo-nome">Nome *</label>
                            <input id="pl-novo-nome" className="rp-input" value={nome} onChange={e => setNome(e.target.value)}
                                placeholder="Nome do contato" disabled={salvando} autoFocus />
                        </div>
                        <div className="rp-two">
                            <div className="rp-field">
                                <label className="rp-label" htmlFor="pl-novo-fone">Telefone</label>
                                <input id="pl-novo-fone" className="rp-input" value={telefone}
                                    onChange={e => setTelefone(formatarTelefone(e.target.value))}
                                    placeholder="+55 (11) 99999-0000" disabled={salvando} />
                            </div>
                            <div className="rp-field">
                                <label className="rp-label" htmlFor="pl-novo-email">E-mail</label>
                                <input id="pl-novo-email" type="email" className="rp-input" value={email}
                                    onChange={e => setEmail(e.target.value)} placeholder="email@exemplo.com" disabled={salvando} />
                            </div>
                        </div>
                    </>
                )}
            </PanelSection>

            <PanelSection title="Organização">
                <div className="rp-field">
                    <OrganizationPicker
                        id="pl-novo-org"
                        value={organizacao.id}
                        valueLabel={organizacao.nome}
                        disabled={salvando}
                        onChange={(id, nomeOrg) => setOrganizacao({ id, nome: nomeOrg })}
                    />
                    <p className="rp-none" style={{ marginTop: 6 }}>
                        {organizacao.id ? 'Com organização, o contato é opcional.' : 'Opcional. Um negócio pode ter contato, organização ou os dois.'}
                    </p>
                </div>
            </PanelSection>

            <PanelSection title="Oportunidade">
                <div className="rp-two">
                    <div className="rp-field">
                        <label className="rp-label">Etapa</label>
                        <Select value={etapa} onChange={setEtapa} options={etapasCriaveis()} showOptionAccessory disabled={salvando} />
                    </div>
                    <div className="rp-field">
                        <label className="rp-label">Temperatura</label>
                        <Select value={temperatura} onChange={setTemperatura} options={TEMPERATURAS}
                            placeholder="Selecionar" showOptionAccessory disabled={salvando} />
                    </div>
                </div>
                <div className="rp-two">
                    <div className="rp-field">
                        <label className="rp-label" htmlFor="pl-novo-valor">Valor estimado</label>
                        <input id="pl-novo-valor" className="rp-input" inputMode="numeric" value={valor}
                            onChange={e => setValor(formatarMoeda(e.target.value))} placeholder="R$ 0,00" disabled={salvando} />
                    </div>
                    <div className="rp-field">
                        <label className="rp-label">Responsável</label>
                        <Select value={responsavelId} onChange={setResponsavelId} options={opcoesUsuario}
                            placeholder="Selecionar" showOptionAccessory searchFrom={8} disabled={salvando} />
                    </div>
                </div>
                <div className="rp-field">
                    <label className="rp-label" htmlFor="pl-novo-obs">Observação</label>
                    <textarea id="pl-novo-obs" className="rp-input is-area" rows={3} value={observacao}
                        onChange={e => setObservacao(e.target.value)} placeholder="Como surgiu essa oportunidade…" disabled={salvando} />
                </div>
            </PanelSection>

            {campos.length > 0 && (
                <PanelSection title="Campos personalizados">
                    {campos.map(campo => (
                        <div className="rp-field" key={campo.campoPersonalizadoId}>
                            <label className="rp-label">{campo.nome}{campo.obrigatorio ? ' *' : ''}</label>
                            {campoPersonalizado(campo)}
                        </div>
                    ))}
                </PanelSection>
            )}
        </RecordPanel>
    );
};

export default CreateOpportunityPanel;
